import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import type { ParticipantRole, PresentationStatus, Registration } from '../store/registrationsSlice'
import { setPresentationUpload } from '../store/registrationsSlice'
import type { AppEvent } from '../store/eventsSlice'
import {
  isTicketActive,
  hasEventEnded,
  loadGoogleFont,
  CURATED_FONTS,
  registerPathWithPrefill,
} from '../utils/event'
import { downloadOwnCertificateOfAttendance, normaliseLanguage } from '../utils/downloadCoa'
import { downloadOwnEventMediaZip } from '../utils/downloadEventMediaZip'
import type { EventMediaType, ParticipantMediaSummary } from '@proaxiv/server-api'
import { MediaGallery, type MediaGalleryItem, type MediaGalleryKind } from '@proaxiv/ui'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { Button } from '../components/Button'
import { TicketPresentationModal } from '../components/TicketPresentationModal'
import { EventHeader, RegistrationDetails, TicketCard, TicketView } from './event/TicketView'
import { formatFullName } from '../utils/participantMappers'
import {
  setPresentationFile,
  downloadPresentationFile,
  hasPresentationFile,
} from '../utils/presentationFiles'
import { forceFileDownload } from '../utils/forceFileDownload'
import { formatEventDateTime } from '../utils/datetime'
import { keycloak } from '../auth/keycloak'
import { fetchWithTimeout } from '../utils/fetchWithTimeout'

// QR codes must always be black-on-white — it's the highest-contrast, most
// reliably scannable combination across cameras and lighting conditions.
const QR_FG_COLOR = '#000000'
const QR_BG_COLOR = '#ffffff'

const PAGE_LOAD_TIME = Date.now()

// ── Participant API types ──────────────────────────────────────────────────────

interface PresentationApiData {
  status?: string | null
  fileName?: string | null
  uploadedAt?: string | null
  reminderSentAt?: string | null
  rejectionMessage?: string | null
  uploadedByOrganizer?: boolean | null
}

interface ParticipantApiData {
  id?: string
  email?: string
  firstName: string
  lastName?: string | null
  companyName?: string | null
  consentDataCollection?: boolean | null
  consentMediaRecording?: boolean | null
  registeredByOrganizer?: boolean | null
  status: 'INVITED' | 'REGISTERED' | 'ACCEPTED' | 'ATTENDED' | 'REJECTED'
  role?: string | null
  rejectionMessage?: string | null
  attendedAt?: string | null
  certificateGeneratedAt?: string | null
  presentation?: PresentationApiData | null
  /** Free-text note the organiser wrote for this participant; shown on their ticket. */
  organizerNote?: string | null
}

/** Maps the backend's uppercase presentation status enum to the frontend's lowercase union. */
function mapPresentationStatus(s?: string | null): PresentationStatus {
  const map: Record<string, PresentationStatus> = {
    NONE: 'none',
    REMINDER_SENT: 'reminder_sent',
    UPLOADED: 'uploaded',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
  }
  return map[s ?? ''] ?? 'none'
}

interface EventApiData {
  title: string
  startDate?: string | null
  endDate?: string | null
  venue?: string | null
  status?: string | null
  primaryColor?: string | null
  backgroundColor?: string | null
  textColor?: string | null
  accentColor?: string | null
  fontFamily?: string | null
  headingFontFamily?: string | null
  ticketAnnouncement?: string | null
  qrActivationDate?: string | null
  /** IANA timezone ID (e.g. "Europe/Rome") the event's wall-clock times must be shown in. */
  timezone?: string | null
}

interface ParticipantProfileResp {
  success: boolean
  participant?: ParticipantApiData
  event?: EventApiData
  errorCode?: string
  message?: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Formats a ticket-page datetime in the event's own timezone (never the viewer's),
 *  using `locale` (the app's current UI language) for month/weekday names and date order.
 *  Omits the timezone suffix — DAJ feedback: ticket page shows wall-clock time only. */
function formatDateTime(iso: string, timezone: string | null | undefined, locale: string): string {
  return formatEventDateTime(
    iso,
    timezone,
    locale,
    {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    { includeTimeZoneName: false }
  )
}

// ── Shared icons ──────────────────────────────────────────────────────────────

const CertificateDownloadIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const ExternalLinkIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
)

const BackArrowIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
)

const LockIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
)

// ── Post-event downloads ──────────────────────────────────────────────────────
// Replaces the entrance QR section once the event has ended: an attended participant's
// Certificate of Attendance and any event media the organizer has shared.

interface PostEventDownloadsProps {
  b: { primary: string; accent: string }
  headingFamily: string
  bodyFamily: string
  t: (key: string) => string
  coaAvailable: boolean
  coaDownloading: boolean
  coaError: string | null
  onDownloadCoa: () => void
  mediaItems: ParticipantMediaSummary[]
  onDownloadMedia: (item: ParticipantMediaSummary) => void
  getMediaPreviewUrl: (item: ParticipantMediaSummary) => Promise<string>
  onDownloadAllMedia: () => void
  mediaZipError: string | null
}

const MEDIA_TYPE_TO_GALLERY_KIND: Record<EventMediaType, MediaGalleryKind> = {
  PHOTO: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  PDF: 'pdf',
}

function PostEventDownloads({
  b,
  headingFamily,
  bodyFamily,
  t,
  coaAvailable,
  coaDownloading,
  coaError,
  onDownloadCoa,
  mediaItems,
  onDownloadMedia,
  getMediaPreviewUrl,
  onDownloadAllMedia,
  mediaZipError,
}: PostEventDownloadsProps) {
  const galleryItems: MediaGalleryItem[] = mediaItems.map((item) => ({
    id: item.id,
    fileName: item.fileName,
    kind: MEDIA_TYPE_TO_GALLERY_KIND[item.mediaType],
    getPreviewUrl: () => getMediaPreviewUrl(item),
  }))
  return (
    <div className="flex w-full flex-col gap-5">
      {/* Certificate of Attendance section */}
      <div className="flex w-full flex-col gap-2.5">
        <p
          className="text-[10px] font-bold tracking-[0.22em] uppercase opacity-40"
          style={{ fontFamily: bodyFamily }}
        >
          {t('ticket_coa_section_title')}
        </p>
        {coaAvailable ? (
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="unstyled"
              onClick={onDownloadCoa}
              disabled={coaDownloading}
              leftIcon={<CertificateDownloadIcon />}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-80 disabled:opacity-50"
              style={{
                background: `${b.primary}18`,
                color: b.primary,
                border: `1px solid ${b.primary}30`,
                fontFamily: headingFamily,
              }}
            >
              {coaDownloading ? t('coa_downloading') : t('ticket_coa_download')}
            </Button>
            {coaError && (
              <p className="text-xs font-medium" style={{ color: '#E61919' }}>
                {coaError}
              </p>
            )}
          </div>
        ) : (
          <p
            className="rounded-xl px-4 py-3 text-center text-sm opacity-40"
            style={{ background: `${b.primary}0d`, border: `1px solid ${b.primary}20` }}
          >
            {t('ticket_coa_pending')}
          </p>
        )}
      </div>

      {/* Event media section */}
      <div className="flex w-full flex-col gap-2.5">
        <p
          className="text-[10px] font-bold tracking-[0.22em] uppercase opacity-40"
          style={{ fontFamily: bodyFamily }}
        >
          {t('ticket_media_section_title')}
        </p>
        <MediaGallery
          items={galleryItems}
          onDownload={(galleryItem) => {
            const item = mediaItems.find((m) => m.id === galleryItem.id)
            if (item) onDownloadMedia(item)
          }}
          downloadLabel={t('ticket_media_download')}
          previewUnavailableLabel={t('ticket_media_preview_unavailable')}
          onDownloadAll={onDownloadAllMedia}
          downloadAllLabel={t('ticket_media_download_all')}
          emptyLabel={
            <p
              className="rounded-xl px-4 py-3 text-center text-sm opacity-40"
              style={{ background: `${b.primary}0d`, border: `1px solid ${b.primary}20` }}
            >
              {t('ticket_media_pending')}
            </p>
          }
          itemClassName="flex w-full flex-col gap-3 rounded-xl px-4 py-2.5"
          itemStyle={{ background: `${b.primary}0d`, border: `1px solid ${b.primary}25` }}
          iconStyle={{ color: b.primary }}
          downloadButtonClassName="shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
          downloadButtonStyle={{
            background: `${b.primary}18`,
            color: b.primary,
            fontFamily: headingFamily,
          }}
          downloadAllButtonClassName="self-end rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80"
          downloadAllButtonStyle={{
            background: `${b.primary}18`,
            color: b.primary,
            fontFamily: headingFamily,
          }}
        />
        {mediaZipError && (
          <p className="text-xs font-medium" style={{ color: '#E61919' }}>
            {mediaZipError}
          </p>
        )}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

/**
 * Standalone ticket page at /e/:id/ticket.
 *
 * Two rendering paths:
 *  - With ?tk=<secretToken>  → participant's personal ticket. Always accessible;
 *    the Entrance QR Code section handles activation gating itself.
 *  - Without ?tk             → generic event ticket / QR (legacy path, keeps existing tests).
 */
export function TicketPage() {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const tk = searchParams.get('tk')
  const openPresentationParam = searchParams.get('presentation') === 'upload'
  // Set by organizer-facing "View Ticket" actions (e.g. AcceptedRowActions) to the exact
  // management-page URL — including its active tab — they were on, so "Back to event" returns
  // there instead of always landing on the management page's default tab.
  const fromParam = searchParams.get('from')

  const [coaDownloading, setCoaDownloading] = useState(false)
  const [coaError, setCoaError] = useState<string | null>(null)
  const [mediaItems, setMediaItems] = useState<ParticipantMediaSummary[]>([])

  // ── Legacy Redux data (PATH B — no ?tk=) ────────────────────────────────────
  const reduxEvent = useAppSelector((s) => s.events.list.find((e) => e.id === id))
  const reduxRegistration = useAppSelector((s) =>
    tk ? s.registrations.list.find((r) => r.secretToken === tk && r.eventId === id) : undefined
  )

  // ── API data (PATH A — ?tk= present) ────────────────────────────────────────
  const [profileResp, setProfileResp] = useState<ParticipantProfileResp | null>(null)
  const [profileLoading, setProfileLoading] = useState(!!tk)

  const loadProfile = useCallback(() => {
    if (!tk) return Promise.resolve()
    return fetch('/api/v1/event/participant/me', {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then((r) => r.json())
      .then((data: ParticipantProfileResp) => {
        setProfileResp(data)
        setProfileLoading(false)
      })
      .catch(() => {
        setProfileResp({
          success: false,
          errorCode: 'FETCH_ERROR',
          message: 'Failed to load your ticket.',
        })
        setProfileLoading(false)
      })
  }, [tk])

  useEffect(() => {
    void loadProfile()
  }, [loadProfile])

  // ── Presentation modal ───────────────────────────────────────────────────────
  // Manual open (Manage click) vs auto-open from `?presentation=upload`. Derived open
  // state avoids sync setState-in-effect when the query param + registration become ready.
  const [manualPresentationOpen, setManualPresentationOpen] = useState(false)
  const [dismissedAutoOpen, setDismissedAutoOpen] = useState(false)
  // Legacy PATH B: IndexedDB file presence, keyed by registration id so stale results
  // never apply after a registration change (eligibility itself is derived below).
  const [legacyFileCheck, setLegacyFileCheck] = useState<{ id: string; has: boolean } | null>(null)

  // ── Derived values ───────────────────────────────────────────────────────────

  const apiParticipant = profileResp?.success ? profileResp.participant : undefined
  const apiEvent = profileResp?.success ? profileResp.event : undefined

  // Map backend status enum to the shapes the existing JSX expects
  const registration: Registration | undefined = useMemo(
    () =>
      apiParticipant
        ? ({
            id: apiParticipant.id ?? tk ?? '',
            eventId: id ?? '',
            email: apiParticipant.email ?? '',
            company: apiParticipant.companyName ?? '',
            consentPersonalData: !!apiParticipant.consentDataCollection,
            consentMedia: !!apiParticipant.consentMediaRecording,
            registeredAt: '',
            registeredByOrganizer: !!apiParticipant.registeredByOrganizer,
            firstName: apiParticipant.firstName,
            lastName: apiParticipant.lastName ?? undefined,
            status: (() => {
              const s = apiParticipant.status
              if (s === 'ACCEPTED' || s === 'ATTENDED') return 'accepted' as const
              if (s === 'REJECTED') return 'rejected' as const
              return 'pending' as const
            })(),
            role: (() => {
              const r = apiParticipant.role
              if (!r) return undefined
              const map: Record<string, ParticipantRole> = {
                AUDIENCE: 'Audience',
                SPEAKER: 'Speaker',
                STAFF: 'Staff',
                HOST: 'Host',
              }
              return map[r] ?? ('Audience' as ParticipantRole)
            })(),
            attended: apiParticipant.status === 'ATTENDED',
            privateNote: apiParticipant.organizerNote ?? undefined,
            secretToken: tk!,
            rejectionMessage: apiParticipant.rejectionMessage ?? undefined,
            presentation: apiParticipant.presentation
              ? {
                  status: mapPresentationStatus(apiParticipant.presentation.status),
                  fileName: apiParticipant.presentation.fileName ?? undefined,
                  uploadedAt: apiParticipant.presentation.uploadedAt ?? undefined,
                  lastReminderAt: apiParticipant.presentation.reminderSentAt ?? undefined,
                  rejectionMessage: apiParticipant.presentation.rejectionMessage ?? undefined,
                  uploadedByOrganizer: apiParticipant.presentation.uploadedByOrganizer ?? undefined,
                }
              : undefined,
          } as Registration)
        : reduxRegistration,
    [apiParticipant, id, reduxRegistration, tk]
  )

  // Only auto-open when the participant still has something to do: they were asked but
  // haven't uploaded yet (`reminder_sent`), or a previous submission was rejected and needs
  // resubmitting. Once a file is uploaded it's either `uploaded` (pending organiser review) or
  // `accepted` — in both cases the popup would just be in the way on every ticket-page load
  // (see #173), since there's nothing left for the participant to submit.
  const presentationNeedsAction =
    registration?.presentation?.status === 'reminder_sent' ||
    registration?.presentation?.status === 'rejected'
  const shouldAutoOpenPresentation =
    openPresentationParam && presentationNeedsAction && !dismissedAutoOpen

  const presentationModalOpen = manualPresentationOpen || shouldAutoOpenPresentation

  const openPresentationModal = () => setManualPresentationOpen(true)
  const closePresentationModal = () => {
    setManualPresentationOpen(false)
    if (openPresentationParam) setDismissedAutoOpen(true)
  }

  const presentationStatus = registration?.presentation?.status
  const legacyDownloadEligible =
    !apiParticipant &&
    !!registration &&
    (presentationStatus === 'uploaded' ||
      presentationStatus === 'accepted' ||
      presentationStatus === 'rejected')

  const legacyCanDownload =
    legacyDownloadEligible &&
    !!registration &&
    legacyFileCheck?.id === registration.id &&
    legacyFileCheck.has

  // Legacy (PATH B) download availability — checked from IndexedDB, refreshed on modal open.
  // Ineligible statuses derive canDownload=false; only the async IndexedDB probe uses setState.
  useEffect(() => {
    if (!legacyDownloadEligible || !registration) return
    const registrationId = registration.id
    let cancelled = false
    hasPresentationFile(registrationId).then((has) => {
      if (!cancelled) setLegacyFileCheck({ id: registrationId, has })
    })
    return () => {
      cancelled = true
    }
  }, [legacyDownloadEligible, presentationModalOpen, presentationStatus, registration])

  const isApiPath = !!apiParticipant

  /** Drops the `?presentation=upload` query param once a file has been submitted — it's
   *  only meant to auto-open the modal for a participant who was just emailed a link asking
   *  them to upload; keeping it in the URL after they've done so would re-trigger the
   *  auto-open logic (and the popup's "still to do" appearance) on every later visit, e.g.
   *  after a page refresh or reopening a bookmarked link (see #173). */
  function clearPresentationQueryParam() {
    if (!openPresentationParam) return
    const next = new URLSearchParams(searchParams)
    next.delete('presentation')
    setSearchParams(next, { replace: true })
  }

  async function handlePresentationUpload(file: File) {
    if (!registration) return
    if (isApiPath && tk) {
      const form = new FormData()
      form.append('file', file)
      // A bounded timeout (rather than a bare `fetch`) — see #225: without one, a stalled upload
      // on a slow mobile connection left the modal's "Submit" button disabled forever with no
      // error, since the request promise never settled.
      const res = await fetchWithTimeout('/api/v1/event/participant/presentation', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tk}` },
        body: form,
      })
      if (!res.ok) throw new Error('upload failed')
      await loadProfile()
    } else {
      await setPresentationFile(registration.id, file)
      dispatch(
        setPresentationUpload({
          id: registration.id,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          status: 'uploaded',
          uploadedByOrganizer: false,
        })
      )
    }
    clearPresentationQueryParam()
  }

  async function handlePresentationDownload() {
    if (!registration) return
    if (isApiPath && tk) {
      const res = await fetch('/api/v1/event/participant/presentation', {
        headers: { Authorization: `Bearer ${tk}` },
      })
      const data = (await res.json()) as {
        success: boolean
        presignedUrl?: string
        presentation?: { fileName?: string }
      }
      if (!data.success || !data.presignedUrl) throw new Error('download unavailable')
      await forceFileDownload(
        data.presignedUrl,
        data.presentation?.fileName ?? registration.presentation?.fileName
      )
    } else {
      await downloadPresentationFile(registration.id)
    }
  }

  // Certificate of Attendance availability. On the real backend API path, the button is shown
  // once `certificateGeneratedAt` is set (EP-13); on the legacy Redux-only path (no backend
  // participant to query), falls back to the Redux-stored `certificateLastSentAt`. Only shown
  // once the participant has actually attended.
  const coaLastSentAt = registration?.certificateLastSentAt
  const coaAvailable =
    !!registration?.attended &&
    (isApiPath ? !!apiParticipant?.certificateGeneratedAt : !!coaLastSentAt)

  // Event media gallery (EP-14/EP-15) — attended participants only. Uses the magic-link
  // participant media endpoints with Bearer tk auth (same pattern as presentation/CoA). The
  // backend additionally gates the response on the organizer's last "Send Event Media" action:
  // it only returns files uploaded at or before that send, so newly uploaded files never
  // appear here until the organizer sends (or re-sends) the notification — no frontend polling
  // shortcut can bypass that.
  const canShowMedia = Boolean(id && registration?.attended)
  useEffect(() => {
    if (!canShowMedia || !tk) return
    let cancelled = false
    fetch('/api/v1/event/participant/media', {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then((r) => r.json())
      .then((data: { success?: boolean; media?: ParticipantMediaSummary[] }) => {
        if (!cancelled && data.success && Array.isArray(data.media)) {
          setMediaItems(data.media)
        } else if (!cancelled) {
          setMediaItems([])
        }
      })
      .catch(() => {
        if (!cancelled) setMediaItems([])
      })
    return () => {
      cancelled = true
    }
  }, [tk, canShowMedia])
  const visibleMediaItems = canShowMedia ? mediaItems : []

  async function handleDownloadMedia(item: ParticipantMediaSummary) {
    if (!tk) return
    try {
      const res = await fetch(`/api/v1/event/participant/media/${item.id}/download`, {
        headers: { Authorization: `Bearer ${tk}` },
      })
      const data = (await res.json()) as {
        success?: boolean
        presignedUrl?: string
        fileName?: string
      }
      if (!data.success || !data.presignedUrl) throw new Error('download unavailable')
      await forceFileDownload(data.presignedUrl, data.fileName ?? item.fileName)
    } catch {
      // Silently ignore — same as presentation download failure on ticket page
    }
  }

  // Resolves the inline preview URL for a single media item on first render (see
  // `MediaGalleryItem.getPreviewUrl`). Reuses the same download endpoint as
  // `handleDownloadMedia` — the backend returns both `presignedUrl` (attachment) and
  // `previewUrl` (inline) in one response — so previews cost no extra request beyond what a
  // click-to-download would already need.
  async function getMediaPreviewUrl(item: ParticipantMediaSummary): Promise<string> {
    if (!tk) throw new Error('missing participant token')
    const res = await fetch(`/api/v1/event/participant/media/${item.id}/download`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
    const data = (await res.json()) as { success?: boolean; previewUrl?: string }
    if (!data.success || !data.previewUrl) throw new Error('preview unavailable')
    return data.previewUrl
  }

  const [mediaZipError, setMediaZipError] = useState<string | null>(null)
  async function handleDownloadAllMedia() {
    if (!tk) return
    setMediaZipError(null)
    try {
      await downloadOwnEventMediaZip(tk)
    } catch {
      setMediaZipError(t('ticket_media_download_all_error'))
    }
  }

  async function handleDownloadCoa() {
    if (!registration || !id) return
    if (!isApiPath || !tk) {
      // Legacy Redux-only demo path has no real backend certificate to download from.
      setCoaError(t('coa_download_error'))
      return
    }
    setCoaDownloading(true)
    setCoaError(null)
    try {
      await downloadOwnCertificateOfAttendance({
        participantToken: tk,
        participantName: `${registration.firstName}${registration.lastName ? ' ' + registration.lastName : ''}`,
        language: normaliseLanguage(i18n.language),
      })
    } catch {
      setCoaError(t('coa_download_error'))
    } finally {
      setCoaDownloading(false)
    }
  }

  // Build a branding-compatible object from either the API event or Redux event
  const b = useMemo(
    () =>
      apiEvent
        ? {
            background: apiEvent.backgroundColor ?? '#0f0e1f',
            primary: apiEvent.primaryColor ?? '#0A7CFF',
            accent: apiEvent.accentColor ?? apiEvent.primaryColor ?? '#0A7CFF',
            text: apiEvent.textColor ?? '#ffffff',
            bodyFont: apiEvent.fontFamily ?? '',
            headingFont: apiEvent.headingFontFamily ?? apiEvent.fontFamily ?? '',
            logo: undefined,
          }
        : reduxEvent?.branding,
    [apiEvent, reduxEvent]
  )

  const pageTitle = apiEvent?.title ?? reduxEvent?.title
  const pageStartDateTime = apiEvent?.startDate ?? reduxEvent?.startDateTime
  const pageAddress = apiEvent?.venue ?? reduxEvent?.address
  const pageTicketAnnouncement = apiEvent?.ticketAnnouncement ?? reduxEvent?.ticketAnnouncement
  // Tickets must always display the event's own timezone, never the viewer's device
  // timezone — see issue #184.
  const pageTimezone = apiEvent?.timezone ?? reduxEvent?.timezone

  const ticketActive = useMemo(() => {
    if (apiEvent) {
      return apiEvent.qrActivationDate
        ? PAGE_LOAD_TIME >= Date.parse(apiEvent.qrActivationDate)
        : true
    }
    return reduxEvent ? isTicketActive(reduxEvent) : false
  }, [apiEvent, reduxEvent])

  // Once the event has ended, the entrance QR code is no longer useful — swap it out
  // for the post-event downloads (CoA + media) instead.
  // Use PAGE_LOAD_TIME (not Date.now) so this stays render-pure for react-hooks/purity.
  const eventEnded = useMemo(() => {
    if (apiEvent) {
      return apiEvent.endDate ? PAGE_LOAD_TIME > Date.parse(apiEvent.endDate) : false
    }
    return reduxEvent ? hasEventEnded(reduxEvent) : false
  }, [apiEvent, reduxEvent])

  const publicUrl = typeof window !== 'undefined' ? `${window.location.origin}/e/${id}` : `/e/${id}`

  // The ticket page is also opened by organizers/admins (e.g. the "View Ticket" action in the
  // participants table) from within the authenticated app, in the same tab/session — not just by
  // anonymous participants following their personal ticket link. `keycloak.authenticated` is
  // already resolved by the time this renders (AuthProvider's silent check-sso runs before public
  // pages mount their children), so it reliably tells apart a logged-in staff viewer from an
  // anonymous ticket holder without needing a dedicated role check.
  const isStaffViewer = keycloak.authenticated === true
  const backToEventPath = fromParam || `/event-planner/${id}`

  useEffect(() => {
    if (!b) return
    loadGoogleFont(b.headingFont)
    loadGoogleFont(b.bodyFont)
  }, [b])

  // ── Loading (API path) ────────────────────────────────────────────────────────
  if (tk && profileLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-950 text-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
      </div>
    )
  }

  // ── Event not found (legacy PATH B — no ?tk=) ─────────────────────────────────
  if (!tk && (!reduxEvent || reduxEvent.status !== 'published')) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-950 text-white">
        <p className="text-5xl font-black tracking-tighter opacity-20">404</p>
        <p className="text-sm text-neutral-400">This event is not available.</p>
      </div>
    )
  }

  // Use API branding when available, otherwise fall back to Redux event branding
  const branding = b ??
    reduxEvent?.branding ?? {
      background: '#0f0e1f',
      primary: '#0A7CFF',
      accent: '#0A7CFF',
      text: '#ffffff',
      bodyFont: '',
      headingFont: '',
      logo: undefined,
    }

  const headingFamily = branding.headingFont
    ? `'${branding.headingFont}', ${CURATED_FONTS.find((f) => f.family === branding.headingFont)?.family ?? 'serif'}`
    : 'serif'
  const bodyFamily = branding.bodyFont ? `'${branding.bodyFont}', sans-serif` : 'sans-serif'

  const cssVars = {
    '--ev-bg': branding.background,
    '--ev-text': branding.text,
    '--ev-primary': branding.primary,
    '--ev-accent': branding.accent,
    '--ev-heading-font': headingFamily,
    '--ev-body-font': bodyFamily,
  } as React.CSSProperties

  // Post-event downloads replace the entrance QR once the event has ended and the participant
  // attended. Built once here and handed to both render paths — the extracted TicketView
  // (PATH A) and the legacy inline card (PATH B) — so the two stay in sync.
  const postEventDownloads = registration?.attended ? (
    <PostEventDownloads
      b={branding}
      headingFamily={headingFamily}
      bodyFamily={bodyFamily}
      t={t}
      coaAvailable={coaAvailable}
      coaDownloading={coaDownloading}
      coaError={coaError}
      onDownloadCoa={() => void handleDownloadCoa()}
      mediaItems={visibleMediaItems}
      onDownloadMedia={(item) => void handleDownloadMedia(item)}
      getMediaPreviewUrl={getMediaPreviewUrl}
      onDownloadAllMedia={() => void handleDownloadAllMedia()}
      mediaZipError={mediaZipError}
    />
  ) : null

  // Shared with the extracted TicketView (accepted-ticket case) so both render the exact
  // same footer beneath the card, rather than duplicating the Link/icon markup.
  const footerLinks = (
    <>
      {isStaffViewer ? (
        <Link
          to={backToEventPath}
          className="mt-6 flex items-center gap-1.5 text-xs font-medium opacity-50 transition-opacity hover:opacity-80"
        >
          <BackArrowIcon />
          {t('ticket_back_to_event')}
        </Link>
      ) : (
        <Link
          to={`/e/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 flex items-center gap-1.5 text-xs font-medium opacity-50 transition-opacity hover:opacity-80"
        >
          <ExternalLinkIcon />
          {t('ticket_view_event_page')}
        </Link>
      )}
    </>
  )

  const pageWrap = (children: React.ReactNode) => (
    <div
      style={{
        ...cssVars,
        background: 'var(--ev-bg)',
        color: 'var(--ev-text)',
        fontFamily: 'var(--ev-body-font)',
      }}
      className="flex min-h-screen flex-col items-center justify-center px-6 py-12"
    >
      <div className="fixed top-4 right-4 z-40">
        <Suspense fallback={null}>
          <LanguageSwitcher align="right" />
        </Suspense>
      </div>
      {children}
      {footerLinks}
    </div>
  )

  // Normalises either data source (API PATH A / Redux PATH B) into the shape TicketView
  // expects, so the same presentational component renders a true WYSIWYG ticket for both
  // a real participant and — with mock data — the event editor's live preview.
  const viewEvent: AppEvent = {
    id: id ?? '',
    title: pageTitle ?? '',
    status: 'published',
    address: pageAddress ?? '',
    startDateTime: pageStartDateTime ?? '',
    text: '',
    ticketAnnouncement: pageTicketAnnouncement ?? undefined,
    ticketActivation: apiEvent?.qrActivationDate ?? reduxEvent?.ticketActivation ?? '',
    timezone: pageTimezone ?? undefined,
    branding,
    createdAt: '',
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PATH A: participant ticket — ?tk= present
  // ════════════════════════════════════════════════════════════════════════════

  if (tk) {
    // Token present but API returned an error or no participant data
    if (profileResp && (!profileResp.success || !registration)) {
      return pageWrap(
        <TicketCard b={branding}>
          <div
            className="flex flex-col items-center gap-3 py-4 text-center"
            style={{ color: branding.text }}
          >
            <span style={{ color: branding.primary, opacity: 0.4 }}>
              <LockIcon />
            </span>
            <p className="text-lg font-bold opacity-60" style={{ fontFamily: headingFamily }}>
              Invalid or expired link
            </p>
            <p className="text-sm opacity-40">
              This access link is not recognised. Please check your confirmation email.
            </p>
          </div>
        </TicketCard>
      )
    }

    // Still loading — spinner rendered above; guard in case registration not yet set
    if (!registration) return null

    // Registration pending
    if (registration.status === 'pending') {
      return pageWrap(
        <TicketCard b={branding}>
          <EventHeader
            title={pageTitle ?? ''}
            startDateTime={pageStartDateTime ?? undefined}
            timezone={pageTimezone}
            address={pageAddress ?? undefined}
            logo={branding.logo}
            primary={branding.primary}
            accent={branding.accent}
            headingFamily={headingFamily}
            locale={i18n.language}
          />
          <div className="h-px w-full opacity-15" style={{ background: branding.primary }} />
          <div className="flex w-full flex-col items-center gap-3 py-2 text-center">
            <span
              className="rounded-full px-3 py-1 text-xs font-bold tracking-wide uppercase"
              style={{ background: `${branding.primary}20`, color: branding.primary }}
            >
              {t('ticket_pending_badge')}
            </span>
            <p className="text-sm opacity-50">{t('ticket_pending_desc')}</p>
            <RegistrationDetails
              b={branding}
              headingFamily={headingFamily}
              fullName={formatFullName(registration.firstName, registration.lastName)}
              email={registration.email}
              company={registration.company}
              registeredByOrganizer={registration.registeredByOrganizer}
              t={t}
            />
          </div>
        </TicketCard>
      )
    }

    // Registration rejected
    if (registration.status === 'rejected') {
      const resubmitPath = registerPathWithPrefill(id ?? '', {
        email: registration.email,
        firstName: registration.firstName,
        lastName: registration.lastName,
        company: registration.company,
        consentPersonalData: registration.consentPersonalData,
        consentMedia: registration.consentMedia,
      })
      return pageWrap(
        <TicketCard b={branding}>
          <EventHeader
            title={pageTitle ?? ''}
            startDateTime={pageStartDateTime ?? undefined}
            timezone={pageTimezone}
            address={pageAddress ?? undefined}
            logo={branding.logo}
            primary={branding.primary}
            accent={branding.accent}
            headingFamily={headingFamily}
            locale={i18n.language}
          />
          <div className="h-px w-full opacity-15" style={{ background: branding.primary }} />
          <div className="flex w-full flex-col items-center gap-3 py-2 text-center">
            <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold tracking-wide text-red-500 uppercase">
              {t('ticket_rejected_badge')}
            </span>
            <p className="text-sm opacity-50">{t('ticket_rejected_desc')}</p>
            {registration.rejectionMessage && (
              <p
                className="mt-1 w-full rounded-xl px-4 py-3 text-left text-sm leading-relaxed opacity-60"
                style={{
                  background: `${branding.primary}10`,
                  border: `1px solid ${branding.primary}18`,
                }}
              >
                {registration.rejectionMessage}
              </p>
            )}
            <RegistrationDetails
              b={branding}
              headingFamily={headingFamily}
              fullName={formatFullName(registration.firstName, registration.lastName)}
              email={registration.email}
              company={registration.company}
              registeredByOrganizer={registration.registeredByOrganizer}
              t={t}
            />

            {/* Primary recovery action — a rejection is not a dead end. One clear, prominent
                next step, worded plainly, so the participant knows exactly what to do without
                needing to re-read a rejection email (see #215). */}
            <div className="mt-1 flex w-full flex-col items-center gap-2">
              <Link
                to={resubmitPath}
                className="flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-bold tracking-wide transition-opacity hover:opacity-90"
                style={{
                  background: branding.accent,
                  color: branding.background,
                  fontFamily: headingFamily,
                }}
              >
                {t('ticket_rejected_resubmit_cta')}
              </Link>
              <p className="text-xs opacity-45">{t('ticket_rejected_resubmit_desc')}</p>
            </div>
          </div>
        </TicketCard>
      )
    }

    // ── Registration accepted — the full participant ticket ────────────────────
    const presentationModal =
      presentationModalOpen &&
      registration.presentation &&
      registration.presentation.status !== 'none' ? (
        <TicketPresentationModal
          status={registration.presentation.status}
          fileName={registration.presentation.fileName}
          uploadedAt={registration.presentation.uploadedAt}
          rejectionMessage={registration.presentation.rejectionMessage}
          canDownload={isApiPath || legacyCanDownload}
          onClose={closePresentationModal}
          onUpload={handlePresentationUpload}
          onDownload={
            registration.presentation.status === 'uploaded' ||
            registration.presentation.status === 'accepted'
              ? handlePresentationDownload
              : undefined
          }
        />
      ) : null

    return (
      <>
        <TicketView
          event={viewEvent}
          registration={registration}
          onManagePresentationClick={openPresentationModal}
          onDownloadPresentation={handlePresentationDownload}
          canDownloadPresentation={
            isApiPath || legacyCanDownload || !!registration.presentation?.fileName
          }
          ticketActive={ticketActive}
          eventEnded={eventEnded}
          postEventDownloads={postEventDownloads}
          footer={footerLinks}
        />
        {presentationModal}
      </>
    )
  }

  // ════════════════════════════════════════════════════════════════════════════
  // PATH B: generic ticket — no ?tk= (legacy / existing tests)
  // ════════════════════════════════════════════════════════════════════════════

  // Show "not active yet" screen when accessed without a token
  if (!ticketActive) {
    return (
      <div
        style={{
          ...cssVars,
          background: 'var(--ev-bg)',
          color: 'var(--ev-text)',
          fontFamily: 'var(--ev-body-font)',
        }}
        className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <p
          className="text-2xl font-black"
          style={{ fontFamily: headingFamily, color: branding.accent }}
        >
          {pageTitle}
        </p>
        <p className="text-sm opacity-50">Ticket is not active yet.</p>
        {reduxEvent?.ticketActivation && (
          <p className="text-xs opacity-40">
            Activates{' '}
            {formatDateTime(reduxEvent.ticketActivation, reduxEvent.timezone, i18n.language)}
          </p>
        )}
      </div>
    )
  }

  return pageWrap(
    <TicketCard b={branding}>
      <EventHeader
        title={pageTitle ?? ''}
        startDateTime={pageStartDateTime ?? undefined}
        timezone={pageTimezone}
        address={pageAddress ?? undefined}
        logo={branding.logo}
        primary={branding.primary}
        accent={branding.accent}
        headingFamily={headingFamily}
        locale={i18n.language}
      />

      <div className="h-px w-full opacity-15" style={{ background: branding.primary }} />

      {/* Private note from organizer */}
      {reduxRegistration?.privateNote && (
        <div
          className="w-full rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
          style={{ background: `${branding.accent}12`, border: `1px solid ${branding.accent}25` }}
        >
          <p className="mb-1 text-[10px] font-bold tracking-widest uppercase opacity-50">
            {t('ticket_private_note_label')}
          </p>
          {reduxRegistration.privateNote}
        </div>
      )}

      {pageTicketAnnouncement && (
        <div
          className="w-full rounded-xl px-4 py-3 text-left text-sm leading-relaxed whitespace-pre-wrap"
          style={{ background: `${branding.primary}12`, border: `1px solid ${branding.primary}20` }}
        >
          {pageTicketAnnouncement}
        </div>
      )}

      {eventEnded ? (
        postEventDownloads
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="rounded-2xl bg-white p-4 shadow-lg">
            <QRCodeSVG
              value={publicUrl}
              size={180}
              level="H"
              fgColor={QR_FG_COLOR}
              bgColor={QR_BG_COLOR}
            />
          </div>
          <p className="text-xs opacity-40">Scan to verify</p>
        </div>
      )}
    </TicketCard>
  )
}
